import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { interval } from 'rxjs';

import { TranslateModule } from '@ngx-translate/core';

import { LoadingSpinnerComponent } from '@shared/loading-spinner/loading-spinner.component';
import {
  CourseCreationProgress,
  CREATION_PHASES,
} from '@core/constants/guided-course-creation.constants';

const PERCENT = {
  START: 1,
  STRUCTURE_CEILING: 8,
  MODULES_END: 90,
  MAX: 99,
  CREEP_SHARE: 0.6,
  EASE: 0.024,
  TICK_MS: 1000,
} as const;

const LABEL_KEYS = {
  STRUCTURE: 'guidedCourseCreation.progress.buildingStructure',
  MODULES: 'guidedCourseCreation.progress.modulesCompleted',
  MODULES_WITH_FAILURES:
    'guidedCourseCreation.progress.modulesCompletedWithFailures',
  FINALIZING: 'guidedCourseCreation.progress.finalizing',
} as const;

@Component({
  selector: 'app-creation-progress',
  templateUrl: './creation-progress.component.html',
  styleUrls: ['./creation-progress.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, LoadingSpinnerComponent],
})
export class CreationProgressComponent {
  readonly progress = input.required<CourseCreationProgress>();

  private readonly percent = signal<number>(PERCENT.START);

  readonly displayPercent = computed(() => Math.round(this.percent()));

  readonly labelKey = computed(() => {
    const { phase, failedModules } = this.progress();

    if (phase === CREATION_PHASES.STRUCTURE) return LABEL_KEYS.STRUCTURE;
    if (phase === CREATION_PHASES.FINALIZING) return LABEL_KEYS.FINALIZING;

    return failedModules
      ? LABEL_KEYS.MODULES_WITH_FAILURES
      : LABEL_KEYS.MODULES;
  });

  readonly labelParams = computed(() => {
    const { completedModules, failedModules, totalModules } = this.progress();

    return {
      completed: completedModules,
      failed: failedModules,
      total: totalModules,
    };
  });

  // The real completions set the target; the ceiling is how far the creep may
  // run ahead of them so the bar keeps moving between responses.
  private readonly bounds = computed(() => {
    const { phase, ratio } = this.progress();

    if (phase === CREATION_PHASES.STRUCTURE) {
      return { target: PERCENT.START, ceiling: PERCENT.STRUCTURE_CEILING };
    }

    if (phase === CREATION_PHASES.FINALIZING) {
      return { target: PERCENT.MODULES_END, ceiling: PERCENT.MAX };
    }

    // Past 1 the ceiling would drop below the target and the bar would step back.
    const span = PERCENT.MODULES_END - PERCENT.STRUCTURE_CEILING;
    const target = PERCENT.STRUCTURE_CEILING + Math.min(1, ratio) * span;

    return {
      target,
      ceiling: target + (PERCENT.MODULES_END - target) * PERCENT.CREEP_SHARE,
    };
  });

  constructor() {
    interval(PERCENT.TICK_MS)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.advance());
  }

  private advance(): void {
    const { target, ceiling } = this.bounds();

    this.percent.update(current => {
      const base = Math.max(current, target);
      return Math.min(ceiling, base + (ceiling - base) * PERCENT.EASE);
    });
  }
}
